<template>
  <div v-if="!!phaseData">
    <field-label
      :label="reportTemplates.length > 0 ? 'Modelo de impressão' : ''"
      titleClass="text-body-1"
      titleColorClass="black--text text--lighten-1"
      class="my-4"
    >
      <div class="d-flex gap-8 align-end flex-wrap">
        <select-printing-template v-model="selectedTemplate" type="report" style="max-width: 400px" />
        <v-btn @click="resetReport" color="primary" :disabled="isFinishedAttendance">Gerar novamente</v-btn>
        <v-btn color="primary" :disabled="!report || report.length === 0" :loading="printing" @click="printPdf">
          Imprimir
        </v-btn>
      </div>
    </field-label>

    <field-label label="Relatório" class="mb-4" titleColorClass="grey--text text--darken-2">
      <text-area :readonly="disabled" v-model="report" :rows="20" :disabled="isFinishedAttendance" />
    </field-label>
    <field-label label="Referências" class="mb-4" titleColorClass="grey--text text--darken-2">
      <text-area :readonly="disabled" v-model="references" :disabled="isFinishedAttendance" />
    </field-label>
  </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex'

import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'

import TextArea from '@/components/TextArea.vue'
import FieldLabel from '@/components/FieldLabel.vue'

import { STATUS_FINISHED } from '@/constants/attendance'
import SelectPrintingTemplate from '@/components/PrintingTemplate/SelectPrintingTemplate.vue'

const {
  mapGetters: attendanceGettersMap,
  mapState: attendanceStateMap,
  mapActions: attendanceActionsMap,
} = createNamespacedHelpers('attendance')

export default {
  extends: BasePhase,
  components: {
    TextArea,
    FieldLabel,
    SelectPrintingTemplate,
  },

  data() {
    return {
      templates: [],
      report: null,
      references: null,
      selectedReportTemplateId: null,
      selectedTemplate: null,
      printing: null,
    }
  },
  computed: {
    ...attendanceGettersMap(['attendance', 'getPhaseDataByKey']),
    ...attendanceStateMap(['isServerSyncedData', 'isLocalSyncedData', 'isSavingData']),

    isFinishedAttendance() {
      return this.attendance?.status === STATUS_FINISHED.value
    },

    diagnosisPhaseData() {
      return this.getPhaseDataByKey('diagnostico')
    },

    diseaseActivityPhaseData() {
      return this.getPhaseDataByKey('atividade_doenca')
    },

    medicineHistory() {
      return Object.values(this.getPhaseDataByKey('tratamentos_previos')?.medicines || {}) || []
    },

    reportTemplates() {
      return this.templates.map((t) => ({ id: t.id, name: t.name }))
    },

    printReportUrl() {
      return `${this.$util.apiURL()}/attendance/report/print/${this.attendance.id}/${this.selectedReportTemplateId}`
    },

    headerText() {
      const patientName = this.attendance.patient.socialName || this.attendance.patient.name
      const patientAge = this.$util.toAge(this.attendance.patient.birthday)

      const diagnosisTimeInYears = this.$util.toAgeNumber(this.diagnosisPhaseData.data_sintomas)

      const thisYear = new Date().getFullYear()

      const diagnosisTimeString =
        diagnosisTimeInYears === 0
          ? `desde ${thisYear}`
          : `há ${diagnosisTimeInYears > 1 ? diagnosisTimeInYears + ' anos' : '1 ano'}`

      return `${patientName}, ${patientAge}, é portador de Urticária Crônica Espontânea, ${diagnosisTimeString}. (CID L50.1)`
    },

    medicalMonitoringText() {
      return '\nFaz acompanhamento regular comigo tendo realizado investigação do quadro de urticária crônica (mais de 6 semanas) com análise clínica e laboratorial complementar.'
    },

    currentMedicinesText() {
      let currentMedicines = this.medicineHistory.filter((m) => m.periods.some((p) => !p.end))
      if (currentMedicines.length === 0) {
        return ''
      }
      return (
        '\nFeito uso de doses máximas dos seguintes medicamentos:' +
        currentMedicines.map((m) => `\n-${m.medicine.name}`).join('')
      )
    },

    flaresText() {
      return 'Apresenta controle parcial das crises mesmo com uso regular dos medicamentos acima e ainda com uso temporário de corticosteróides orais e injetáveis nas crises. Esses episódios cursam com lesões visíveis de pele e muito prurido, o que está causando alto impacto negativo em sua qualidade de vida.'
    },

    scoresText() {
      const uasScore = this.diseaseActivityPhaseData.uas7.value

      return `\nAtualmente o critério de gravidade UAS 7 é de ${uasScore} (Esse SCORE é considerado grave acima de 28 pontos).`
    },

    treatmentGuidelineText() {
      return '\nConforme o guideline europeu e Consenso Brasileiro de Urticária Crônica Espontânea (referências abaixo), caso o paciente tenha falhado ao tratamento com anti-histamínicos em doses otimizadas associado à corticóides orais nas exacerbações, está indicado o tratamento com omalizumabe. '
    },

    treatmentRequestText() {
      const uasScore = this.diseaseActivityPhaseData.uas7.value

      if (uasScore >= 28) {
        return '\nPortanto, como paciente apresenta UAS 7 maior que 28, falha à terapia de dose quadruplicada de anti-histamínico, é indicado a associação com tratamento com omalizumabe. Solicito omalizumabe para 4 meses de tratamento. Se não houver resposta, o tratamento será interrompido. Caso houver boa resposta, manterei o tratamento até o 6o mês e só será reintroduzido se recidiva da doença após a suspensão.'
      } else {
        return ''
      }
    },

    referencesText() {
      const paragraphs = []
      paragraphs.push(
        '\nAllergy - 2021 - Zuberbier - The international EAACI GA LEN EuroGuiDerm APAAACI guideline for the definition ',
      )

      paragraphs.push('\n')

      paragraphs.push(
        '\nConsensus on the diagnostic and therapeutic management of chronic spontaneous urticaria in adults – Brazilian Society of Dermatology*',
      )

      return paragraphs.join('')
    },
    defaultReport() {
      return this.getReportText()
    },
    defaultReferences() {
      return this.referencesText
    },
  },
  methods: {
    ...attendanceActionsMap(['startServerSyncing', 'endServerSyncing']),
    async printPdf() {
      await this.saveReport()
      try {
        this.printing = true
        const blob = await this.$api.printingTemplate.print({
          templateId: this.selectedTemplate.id,
          patientId: this.attendance.patient.id,
          text: this.report,
          extraText: this.references,
        })
        this.$util.downloadBlob(blob, 'relatório.pdf')
      } catch (error) {
        this.$root.error(error)
      } finally {
        this.printing = false
      }
    },
    getDefaultData() {
      const data = {}
      return data
    },

    getReportText() {
      const paragraphs = []

      paragraphs.push(this.headerText)

      paragraphs.push(this.medicalMonitoringText)

      paragraphs.push(this.currentMedicinesText)

      paragraphs.push(this.scoresText)

      paragraphs.push(this.treatmentGuidelineText)

      paragraphs.push(this.treatmentRequestText)

      return paragraphs.join('')
    },

    fillDefaultReportAndReferences() {
      this.report = this.defaultReport
      this.references = this.defaultReferences
    },

    async resetReport() {
      if (await this.$root.confirm('Deseja gerar o relatório novamente? As alterações serão perdidas!')) {
        this.fillDefaultReportAndReferences()
      }
    },

    async saveReport() {
      if (this.isServerSyncedData || this.isSavingData) {
        return
      }

      try {
        await this.startServerSyncing()
        await this.$api.patchAttendance(this.attendance.id, this.attendance)
        await this.endServerSyncing(true)
      } catch (error) {
        await this.endServerSyncing(false)
        if (showError) {
          this.$root.error(error)
        } else {
          this.$logger.warn('Não foi possível salvar os dados do atendimento no servidor!', error)
        }
      }

      return Promise.resolve({})
    },

    async loadData() {
      this.templates = (await this.$api.getTemplates()).items

      if (this.reportTemplates.length > 0) {
        this.selectedReportTemplateId = this.reportTemplates[0].id
      }
    },
  },
  async mounted() {
    await this.loadData()
  },
}

// NOME DO PACIENTE, X anos, é portador de Urticária Crônica Espontânea, há X anos. (CID L50.1)

// Faz acompanhamento regular comigo tendo realizado investigação do quadro de urticária crônica (mais de 6 semanas) com análise clínica e laboratorial complementar.
// Feito uso de doses máximas dos seguintes medicamentos:
// -TRAZER LISTA DE MEDICAMENTOS CADA UM EM UMA LINHA SEM O PERÍODO DE USO
// -
// -

// Apresenta controle parcial das crises mesmo com uso regular dos medicamentos acima e ainda com uso temporário de corticosteróides orais e injetáveis nas crises. Esses episódios cursam com lesões visíveis de pele e muito prurido, o que está causando alto impacto negativo em sua qualidade de vida.
// Atualmente o critério de gravidade UAS 7 é de X (Esse SCORE é considerado grave acima de 28 pontos).
// Conforme o guideline europeu e Consenso Brasileiro de Urticária Crônica Espontânea (referências abaixo), caso o paciente tenha falhado ao tratamento com anti-histamínicos em doses otimizadas associado à corticóides orais nas exacerbações, está indicado o tratamento com omalizumabe.
// Portanto, como paciente apresenta UAS 7 maior que 28, falha à terapia de dose quadruplicada de anti-histamínico, é indicado a associação com tratamento com omalizumabe. Solicito omalizumabe para 4 meses de tratamento. Se não houver resposta, o tratamento será interrompido. Caso houver boa resposta, manterei o tratamento até o 6o mês e só será reintroduzido se recidiva da doença após a suspensão.

// Referência bibliográfica
</script>

<style scoped></style>
